import { Injectable, OnDestroy } from '@angular/core';
import { HubConnection } from '@microsoft/signalr';
import { HubConnectionBuilder } from '@microsoft/signalr/dist/esm/HubConnectionBuilder';
import { IHttpConnectionOptions } from '@microsoft/signalr/dist/esm/IHttpConnectionOptions';
import { Observable, Subscription } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { EnvironmentService } from 'src/app/library/services/core/environment.service';
import { UserAuthService } from 'src/app/modules/auth/services/user.service';
import { NotificationPopupComponent } from 'src/app/modules/notifications/dialogs/notification-popup/notification-popup.component';
import { INotificationResponseModel } from 'src/app/modules/notifications/interfaces/notification-response-model';
import { NotificationStatus } from '../../../library/enums/notifications/notifications.enum';
import { SnackbarService } from '../../../library/services/general/snackbar.service';
import { INotificationBaseSearchRequestModel } from '../interfaces/notification-base-search-request.model';
import { INotificationSearchResponseWrapper } from '../interfaces/notification-search-response-wrapper';

@Injectable({
    providedIn: 'root'
})
export class NotificationService implements OnDestroy {
    private connection: HubConnection;
    private _notificationsCache = new BehaviorSubject<
        INotificationResponseModel[]
    >([]);
    private _unreadNotificationCount = new BehaviorSubject<number | null>(null);
    private _unreadNotifications = new BehaviorSubject<
        INotificationResponseModel[] | null
    >(null);

    private _lastNotificationId = new BehaviorSubject<number | null>(null);
    private _scrollLoading = new BehaviorSubject<boolean>(false);
    private _subscriptions = new Subscription();

    constructor(
        private userAuthService: UserAuthService,
        private snackbarService: SnackbarService,
        private environmentService: EnvironmentService
    ) {
        this._subscriptions = this.userAuthService.accessToken$.subscribe(
            token => {
                if (token) {
                    this.initConnection(token);
                }
            }
        );
    }

    public get scrollLoading$(): Observable<boolean> {
        return this._scrollLoading.asObservable();
    }

    public set scrollLoading(value: boolean) {
        this._scrollLoading.next(value);
    }

    get unreadNotifications$(): Observable<INotificationResponseModel[]> {
        return this._unreadNotifications.asObservable();
    }
    get unreadNotificationCount$(): Observable<number> {
        return this._unreadNotificationCount.asObservable();
    }

    get notifications$(): Observable<INotificationResponseModel[]> {
        return this._notificationsCache.asObservable();
    }

    private increaseUnreadNotificationCount(
        unreadNotificationCount: number
    ): void {
        this._unreadNotificationCount.next(unreadNotificationCount + 1);
    }

    private decreaseUnreadNotificationCount(
        unreadNotificationCount: number
    ): void {
        this._unreadNotificationCount.next(unreadNotificationCount - 1);
    }

    public readNotification(notificationId: number): void {
        const cacheValue = this._notificationsCache.value;
        let notification = cacheValue.find(
            g => g.notificationId == notificationId
        );

        let currentUnreadNotificationCount =
            this._unreadNotificationCount.value;
        this.decreaseUnreadNotificationCount(currentUnreadNotificationCount);

        notification.notificationStatusId = NotificationStatus.Read;
        if (notification) {
            this._notificationsCache.next(cacheValue);
            this.connection
                .invoke('ReadNotification', notificationId)
                .catch(err => {
                    //rollback
                    this._unreadNotificationCount.next(
                        currentUnreadNotificationCount
                    );
                    notification.notificationStatusId =
                        NotificationStatus.Unread;
                    this._notificationsCache.next(cacheValue);
                    console.log('Error while calling ReadNotification: ' + err);
                });
        }
    }

    public readAllUnreadNotifications(): void {
        const currentUnreadNotificationCount =
            this._unreadNotificationCount.value;
        const cacheValue = this._notificationsCache.value;
        let unreadNotifications = cacheValue.filter(
            g => g.notificationStatusId == NotificationStatus.Unread
        );
        unreadNotifications.forEach(
            n => (n.notificationStatusId = NotificationStatus.Read)
        );

        this._unreadNotificationCount.next(0);
        this._notificationsCache.next(cacheValue);
        this.connection.invoke('ReadAllUnreadNotifications').catch(err => {
            //rollback
            unreadNotifications.forEach(
                n => (n.notificationStatusId = NotificationStatus.Unread)
            );
            this._notificationsCache.next(cacheValue);
            console.log(
                'Error while calling ReadAllUnreadNotifications: ' + err
            );
            this._unreadNotificationCount.next(currentUnreadNotificationCount);
        });
    }

    public unreadNotification(notificationId: number): void {
        const cacheValue = this._notificationsCache.value;
        let notification = cacheValue.find(
            g => g.notificationId == notificationId
        );

        let currentUnreadNotificationCount =
            this._unreadNotificationCount.value;
        this.increaseUnreadNotificationCount(currentUnreadNotificationCount);
        notification.notificationStatusId = NotificationStatus.Unread;

        if (notification) {
            this._notificationsCache.next(cacheValue);
            this.connection
                .invoke('UnreadNotification', notificationId)
                .catch(err => {
                    //rollback
                    this._unreadNotificationCount.next(
                        currentUnreadNotificationCount
                    );
                    notification.notificationStatusId = NotificationStatus.Read;
                    this._notificationsCache.next(cacheValue);
                    console.log(
                        'Error while calling UnreadNotification: ' + err
                    );
                });
        }
    }

    // Return a bool promise to tell if the connection started correctly.
    // This helps us when we trying to hook up the listener and get the initial notification list
    private initConnection(token: string): Promise<boolean> {
        const options: IHttpConnectionOptions = {
            accessTokenFactory: () => {
                return token;
            }
        };

        this.connection = new HubConnectionBuilder()
            .withUrl(
                `${this.environmentService.environmentConfig.notificationsApiUrl}notifications`,
                options
            )
            .withAutomaticReconnect()
            .build();

        let conn = this.connection
            .start()
            .then(() => {
                console.log(
                    'Notifications manager signalR connection started.'
                );

                this.GetNotificationsFromLastId();
                this.onReceiveNotificationListener();
                this.notificationListener();
                return new Promise<boolean>(resolve => resolve(true));
            })
            .catch(err => {
                console.log(
                    'Error while starting notifications manager connection: ',
                    err.message
                );
                return new Promise<boolean>(resolve => resolve(false));
            });

        return conn;
    }

    private notificationListener(): void {
        this._notificationsCache.subscribe(notifications => {
            if (notifications) {
                const unread = notifications.filter(
                    g => g.notificationStatusId == NotificationStatus.Unread
                );

                this._unreadNotifications.next(unread);
            }
        });
    }

    public GetNotificationsFromLastId(): void {
        this.scrollLoading = true;

        const searchRequest = {
            lastNotificationId: this._lastNotificationId.value,
            pageSize: 20
        } as INotificationBaseSearchRequestModel;

        this.connection
            .invoke('GetNotificationsFromLastId', searchRequest)
            .then((response: INotificationSearchResponseWrapper) => {
                this.setLastNotificationId(response.data);
                this._unreadNotificationCount.next(response.totalUnread);
                this._notificationsCache.next([
                    ...this._notificationsCache.value,
                    ...response.data
                ]);
            })
            .catch(err => {
                console.log(
                    'Error while calling GetPaginatedNotifications: ' + err
                );
                throw err;
            })
            .finally(() => {
                this.scrollLoading = false;
            });
    }

    private setLastNotificationId(
        notifications: INotificationResponseModel[]
    ): void {
        if (notifications.length > 0) {
            this._lastNotificationId.next(
                notifications.slice(-1)[0].notificationId
            );
        } else {
            this._lastNotificationId.next(null);
        }
    }

    private onReceiveNotificationListener(): void {
        if (this.connection)
            this.connection.on(
                'ReceiveNotification',
                (notification: INotificationResponseModel) => {
                    const cacheVal = this._notificationsCache.value;
                    this._notificationsCache.next([notification, ...cacheVal]);
                    const unreadNotificationCount =
                        this._unreadNotificationCount.value;
                    this.increaseUnreadNotificationCount(
                        unreadNotificationCount
                    );
                    this.snackbarService.showPopup(
                        NotificationPopupComponent,
                        notification
                    );
                }
            );
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribe;
        if (this.connection) {
            this.connection.stop();
        }
    }
}
